const BASE = process.env.REACT_APP_API_BASE_URL || '';

// Auth token for iframe contexts (where cookies don't work)
// Prioritize URL hash (always fresh — set just before reload) over sessionStorage (may be stale)
let _authToken = null;
try {
  const hash = window.location.hash.substring(1);
  const params = new URLSearchParams(hash);
  const hashToken = params.get('authToken');
  if (hashToken) _authToken = hashToken;
} catch {}
if (!_authToken) {
  try { _authToken = sessionStorage.getItem('authToken'); } catch {}
}
export function setAuthToken(token) {
  _authToken = token;
  try { sessionStorage.setItem('authToken', token); } catch {}
}
export function getAuthToken() { return _authToken; }

async function jsonFetch(path, init = {}) {
  const { body, headers, ...rest } = init;

  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...rest,
    headers: {
      ...(headers || {}),
      ...(body && !((headers || {})['Content-Type']) ? { 'Content-Type': 'application/json' } : {}),
      ...(_authToken ? { 'x-auth-token': _authToken } : {}),
    },
    body: body != null ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try { data = await res.json(); } catch { data = null; }

  if (!res.ok) {
    let msg = (data && (data.message || data.error)) || `Request failed: ${res.status}`;
    // Handle structured Zoho API errors with details (can be in data.error or data.message)
    const errorObj = data?.error || data?.message;
    if (errorObj && errorObj.details && Array.isArray(errorObj.details) && errorObj.details.length > 0) {
      let detail = errorObj.details[0];
      // Handle nested array structure: details: [[Object]]
      if (Array.isArray(detail) && detail.length > 0) {
        detail = detail[0];
      }
      if (detail && detail.message) {
        msg = detail.message;
      }
    }
    throw new Error(msg);
  }
  return data;
}

export const API = {
  // Auth
  checkAuth: () => jsonFetch('/check-auth'),
  loginPortal: (portal) => jsonFetch(`/login?portal=${portal || ''}`),
  logout: () => jsonFetch('/logout', { method: 'POST' }),
  ping: () => jsonFetch('/ping'),

  // Projects and timelogs
  fetchProjects: () => jsonFetch('/projects'),
  fetchProjectsTimelogs: (employeeZuid) =>
    jsonFetch(`/timelogs${employeeZuid ? `?employeeZuid=${employeeZuid}` : ''}`),
  refreshTimelogs: (employeeZuid) =>
    jsonFetch(`/timelogs?refresh=true${employeeZuid ? `&employeeZuid=${employeeZuid}` : ''}`),
  fetchPeopleTimelogs: (employeeEmail) =>
    jsonFetch(`/people-timelogs${employeeEmail ? `?employeeEmail=${encodeURIComponent(employeeEmail)}` : ''}`),
  refreshPeopleTimelogs: (employeeEmail) =>
    jsonFetch(`/people-timelogs?refresh=true${employeeEmail ? `&employeeEmail=${encodeURIComponent(employeeEmail)}` : ''}`),

  // Zoho Projects timelogs
  createTimelog: (payload) => jsonFetch('/send-timelog', { method: 'POST', body: payload }),
  updateTimelog: (payload) => jsonFetch('/update-timelog', { method: 'PUT', body: payload }),
  deleteTimelog: (payload) => jsonFetch('/delete-timelog', { method: 'DELETE', body: payload }),

  // Tasks by project
  fetchTasksByProject: (projectId) =>
    jsonFetch(`/get-tasks-by-project?projectId=${encodeURIComponent(projectId)}`),

  // Zoho People timelogs
  deletePeopleTimelog: (payload) => jsonFetch('/delete-people-timelog', { method: 'DELETE', body: payload }),

  // CRM Accounts
  searchAccounts: (query) =>
    jsonFetch(`/search-accounts?q=${encodeURIComponent(query)}`),
  fetchAccountProjects: (accountId) =>
    jsonFetch(`/account-projects?accountId=${encodeURIComponent(accountId)}`),
  fetchNonBemandingTasks: (projectId) =>
    jsonFetch(`/non-bemanding-tasks?projectId=${encodeURIComponent(projectId)}`),
  fetchAssignedProjects: () =>
    jsonFetch('/assigned-projects'),
  fetchProjectAccount: (projectId) =>
    jsonFetch(`/project-account?projectId=${encodeURIComponent(projectId)}`),
  refreshAssignedProjects: () =>
    jsonFetch('/assigned-projects?refresh=true'),
  refreshProjectsList: () =>
    jsonFetch('/projects?refresh=true'),

  // Zoho People leave
  fetchLeaveTypes: () => jsonFetch('/leave-types'),
  fetchLeaveRequests: (employeeZuid) =>
    jsonFetch(`/leave-requests${employeeZuid ? `?employeeZuid=${employeeZuid}` : ''}`),
  fetchLeaveBalance: () => jsonFetch('/leave-balance'),
  clearUsersCache: () => jsonFetch('/clear-users-cache', { method: 'POST' }),
  createLeave: (payload) => jsonFetch('/send-leave', { method: 'POST', body: payload }),

  // User preferences
  getUserViewPreference: () => jsonFetch('/user/view-preference'),
  saveUserViewPreference: (view) => jsonFetch('/user/view-preference', { method: 'POST', body: { view } }),

  // Timer (project)
  fetchTimer: () => jsonFetch('/timer'),
  startTimer: () => jsonFetch('/timer/start', { method: 'POST' }),
  stopTimer: () => jsonFetch('/timer/stop', { method: 'POST' }),

  // Workday timer (separate from project timer)
  fetchWorkdayTimer: () => jsonFetch('/timer/workday'),
  startWorkdayTimer: () => jsonFetch('/timer/workday/start', { method: 'POST' }),
  stopWorkdayTimer: () => jsonFetch('/timer/workday/stop', { method: 'POST' }),
  fetchWorkdayPresence: () => jsonFetch('/timer/workday/all'),

  // Zoho People timelog creation / update
  createPeopleTimelog: (payload) => jsonFetch('/send-timelog-people', { method: 'POST', body: payload }),
  updatePeopleTimelog: (payload) => jsonFetch('/update-people-timelog', { method: 'PUT', body: payload }),

  // Task owner assignment
  assignRolle: (projectId, taskId) => jsonFetch('/assign-rolle', { method: 'POST', body: { projectId, taskId } }),
  removeRolle: (projectId, taskId) => jsonFetch('/remove-rolle', { method: 'POST', body: { projectId, taskId } }),
  updateTaskStatus: (projectId, taskId, statusId) => jsonFetch('/task-status', { method: 'POST', body: { projectId, taskId, statusId } }),
};
